import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  NgZone,
  OnDestroy,
  OnInit,
  PLATFORM_ID,
  computed,
  inject,
  signal,
  viewChild,
} from '@angular/core';
import { CommonModule, isPlatformBrowser } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { ApiService } from '../../../../core/services/api.service';
import { API_ROUTES } from '../../../../core/constants/api-routes';
import { LayoutService } from '../../../../core/services/layout.service';
import { S3ImageUploadService } from '../../../../core/services/azure-image-upload.service';
import { CreateAndPublishStoryRequest, EmotionalTone, FamilyMember, FamilySpace } from '../../../../core/models/story.models';

// -- Types --------------------------------------------------------------------

type SharingOption  = 'public' | 'private' | 'family';
type TranscriptTab  = 'verbatim' | 'narrative';

interface StoryReviewNavState {
  videoUrl?:            string | null;
  imageUrl?:            string | null;
  mediaType?:           string;
  storyTitle?:          string;
  transcriptVerbatim?:  string;
  transcriptNarrative?: string;
  categoryId?:          number | null;
  questions?:           any[];
  questionTimeframes?:  Record<string, { questionId: string; startSecond: number; endSecond: number }>;
  videoDuration?:       number;
}

// -- Component ----------------------------------------------------------------

@Component({
  selector: 'app-story-review-video',
  templateUrl: './story-review-video.html',
  styleUrl: './story-review-video.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [CommonModule, FormsModule],
})
export class StoryReviewVideo implements OnInit, AfterViewInit, OnDestroy {
  private readonly router        = inject(Router);
  private readonly platformId    = inject(PLATFORM_ID);
  private readonly api           = inject(ApiService);
  private readonly layoutService = inject(LayoutService);
  private readonly s3Upload      = inject(S3ImageUploadService);
  private readonly zone          = inject(NgZone);
  private readonly cdr           = inject(ChangeDetectorRef);

  // -- View references -------------------------------------------------------

  readonly videoRef          = viewChild<ElementRef<HTMLVideoElement>>('reviewVideo');
  readonly trimCanvas        = viewChild<ElementRef<HTMLCanvasElement>>('trimCanvas');
  readonly trimContainer     = viewChild<ElementRef<HTMLDivElement>>('trimContainer');
  readonly audioRef          = viewChild<ElementRef<HTMLAudioElement>>('reviewAudio');
  readonly audioTrimContainer = viewChild<ElementRef<HTMLDivElement>>('audioTrimContainer');

  // -- Video & media ---------------------------------------------------------

  readonly videoUrl   = signal<string | null>(null);
  readonly imageUrl   = signal<string | null>(null);
  readonly mediaType  = signal<string>('video');
  readonly storyTitle = signal('');
  readonly showReloadWarning    = signal(false);
  readonly showPublishConfirm   = signal(false);
  readonly isPublishing         = signal(false);
  /** True while re-encoding the trimmed segment before S3 upload. */
  readonly isTrimmingMedia      = signal(false);
  /** 0–100 progress while re-encoding the trimmed segment. */
  readonly trimmingProgress     = signal(0);
  readonly titleError           = signal(false);
  readonly publishError         = signal<string | null>(null);
  readonly categoryId           = signal<number>(0);
  readonly reviewQuestions      = signal<any[]>([]);
  readonly reviewTimeframes     = signal<Record<string, { questionId: string; startSecond: number; endSecond: number }>>({});
  readonly reviewVideoDuration  = signal<number>(0);

  // -- Tagging & sharing -----------------------------------------------------

  readonly familySpaces            = signal<FamilySpace[]>([]);
  readonly familyMembers           = signal<FamilyMember[]>([]);
  readonly selectedFamilySpaceId   = signal<string>('all');
  readonly selectedTaggedMemberIds = signal<string[]>([]);
  readonly sharingOption           = signal<SharingOption>('family');
  readonly selectedSharedMemberIds = signal<string[]>([]);
  readonly memberImageErrors       = signal<Record<string, boolean>>({});
  readonly selectedSharingFamilyId = signal<string>('all');
  readonly sharingMemberError      = signal<string | null>(null);

  readonly filteredFamilyMembers = computed(() => {
    const familyId = this.selectedFamilySpaceId();
    const members  = this.familyMembers();
    return familyId === 'all' ? members : members.filter(m => m.familySpaceId === familyId);
  });

  readonly filteredSharingMembers = computed(() => {
    const familyId = this.selectedSharingFamilyId();
    const members  = this.familyMembers();
    return familyId === 'all' ? members : members.filter(m => m.familySpaceId === familyId);
  });

  /** All explicitly selected sharing member IDs, independent of the current view filter. */
  readonly activeSharedIds = computed(() => this.selectedSharedMemberIds());

  /** Family spaces that have at least one member — used in sharing grouped view. */
  readonly familyGroupsForSharing = computed(() =>
    this.familySpaces()
      .map(space => ({
        space,
        members: this.familyMembers().filter(m => m.familySpaceId === space.familySpaceId),
      }))
      .filter(g => g.members.length > 0),
  );

  // -- Story details ---------------------------------------------------------

  readonly storyDate      = signal(new Date().toISOString().split('T')[0]);
  readonly storyDateError = signal<string | null>(null);
  readonly storyLocation  = signal('');
  readonly emotionalToneId   = signal<number>(0);
  readonly emotionalTones    = signal<EmotionalTone[]>([]);

  // -- Custom date picker ----------------------------------------------------

  private static readonly MONTH_NAMES = [
    'January','February','March','April','May','June',
    'July','August','September','October','November','December',
  ];

  readonly showDatePicker  = signal(false);
  readonly pickerDropUp    = signal(false);
  readonly pickerViewYear  = signal(new Date().getFullYear());
  readonly pickerViewMonth = signal(new Date().getMonth()); // 0-indexed

  readonly pickerMonthLabel = computed(() =>
    `${StoryReviewVideo.MONTH_NAMES[this.pickerViewMonth()]} ${this.pickerViewYear()}`,
  );

  readonly storyDateDisplay = computed(() => {
    const val = this.storyDate();
    if (!val) return '';
    const parts = val.split('-');
    const y = parseInt(parts[0], 10);
    const m = parseInt(parts[1], 10);
    const d = parseInt(parts[2], 10);
    if (!y || !m || !d) return val;
    return `${StoryReviewVideo.MONTH_NAMES[m - 1]} ${d}, ${y}`;
  });

  readonly pickerCalendarDays = computed(() => {
    const year      = this.pickerViewYear();
    const month     = this.pickerViewMonth();
    const firstDay  = new Date(year, month, 1).getDay();
    const totalDays = new Date(year, month + 1, 0).getDate();
    const today     = new Date(); today.setHours(0, 0, 0, 0);
    const selVal    = this.storyDate();
    const selected  = selVal ? new Date(selVal + 'T00:00:00') : null;

    type DayCell = { date: Date | null; day: number | null; isCurrentMonth: boolean; isToday: boolean; isSelected: boolean; isDisabled: boolean };
    const cells: DayCell[] = [];

    for (let i = 0; i < firstDay; i++) {
      cells.push({ date: null, day: null, isCurrentMonth: false, isToday: false, isSelected: false, isDisabled: true });
    }
    for (let d = 1; d <= totalDays; d++) {
      const date = new Date(year, month, d);
      date.setHours(0, 0, 0, 0);
      cells.push({
        date,
        day: d,
        isCurrentMonth: true,
        isToday: date.getTime() === today.getTime(),
        isSelected: selected ? date.getTime() === selected.getTime() : false,
        isDisabled: date > today,
      });
    }
    while (cells.length % 7 !== 0) {
      cells.push({ date: null, day: null, isCurrentMonth: false, isToday: false, isSelected: false, isDisabled: true });
    }
    return cells;
  });

  readonly pickerCanGoNext = computed(() => {
    const now = new Date();
    return !(this.pickerViewYear() === now.getFullYear() && this.pickerViewMonth() >= now.getMonth());
  });

  openDatePicker(event: MouseEvent): void {
    const val = this.storyDate();
    if (val) {
      const [y, m] = val.split('-').map(Number);
      this.pickerViewYear.set(y);
      this.pickerViewMonth.set(m - 1);
    }
    // Determine whether to flip upward based on available space below trigger
    const trigger = (event.currentTarget as HTMLElement);
    const rect    = trigger.getBoundingClientRect();
    const spaceBelow = window.innerHeight - rect.bottom;
    const dropdownHeight = 320; // approximate picker height in px
    this.pickerDropUp.set(spaceBelow < dropdownHeight && rect.top > dropdownHeight);
    this.showDatePicker.set(true);
    this.cdr.markForCheck();
  }

  closeDatePicker(): void {
    this.showDatePicker.set(false);
    this.cdr.markForCheck();
  }

  selectPickerDate(date: Date | null): void {
    if (!date) return;
    const today = new Date(); today.setHours(0, 0, 0, 0);
    if (date > today) return;
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, '0');
    const d = String(date.getDate()).padStart(2, '0');
    const value = `${y}-${m}-${d}`;
    this.storyDate.set(value);
    this.validateStoryDate(value);
    this.showDatePicker.set(false);
    this.cdr.markForCheck();
  }

  pickerPrevMonth(): void {
    if (this.pickerViewMonth() === 0) {
      this.pickerViewMonth.set(11);
      this.pickerViewYear.update(y => y - 1);
    } else {
      this.pickerViewMonth.update(v => v - 1);
    }
  }

  pickerNextMonth(): void {
    if (!this.pickerCanGoNext()) return;
    if (this.pickerViewMonth() === 11) {
      this.pickerViewMonth.set(0);
      this.pickerViewYear.update(y => y + 1);
    } else {
      this.pickerViewMonth.update(v => v + 1);
    }
  }

  onStoryDateChange(value: string): void {
    this.storyDate.set(value);
    this.validateStoryDate(value);
  }

  onDateInputBlur(event: FocusEvent): void {
    const input = event.target as HTMLInputElement;
    this.storyDate.set(input.value);
    this.validateStoryDate(input.value);
    this.cdr.markForCheck();
  }

  private validateStoryDate(value: string): void {
    if (!value) {
      this.storyDateError.set('Please enter a valid date.');
      return;
    }
    const parts = value.split('-');
    const year  = parseInt(parts[0], 10);
    const currentYear = new Date().getFullYear();
    if (isNaN(year) || year < 1000 || year > currentYear) {
      this.storyDateError.set(
        year > currentYear
          ? `Year cannot be in the future. Please enter a year up to ${currentYear}.`
          : 'Please enter a valid year (1000 or later).',
      );
    } else {
      this.storyDateError.set(null);
    }
  }

  // -- Transcript ------------------------------------------------------------

  readonly transcriptTab       = signal<TranscriptTab>('verbatim');
  readonly transcriptEditMode  = signal(false);
  readonly transcriptVerbatim  = signal('');
  readonly transcriptNarrative = signal('');

  // -- Trim (video) ----------------------------------------------------------

  readonly trimStart     = signal(0);
  readonly trimEnd       = signal(0);
  readonly videoDuration = signal(0);
  readonly trimApplied   = signal(false);

  readonly activeTranscriptText = computed(() =>
    this.transcriptTab() === 'verbatim' ? this.transcriptVerbatim() : this.transcriptNarrative(),
  );

  readonly trimStartPercent      = computed(() => this.videoDuration() > 0 ? (this.trimStart() / this.videoDuration()) * 100 : 0);
  readonly trimEndPercent        = computed(() => this.videoDuration() > 0 ? (this.trimEnd()   / this.videoDuration()) * 100 : 100);
  readonly trimSelectionWidth    = computed(() => this.trimEndPercent() - this.trimStartPercent());
  readonly trimEndExcludePercent = computed(() => 100 - this.trimEndPercent());
  readonly trimmedDuration       = computed(() => Math.max(0, this.trimEnd()       - this.trimStart()));
  readonly removedDuration       = computed(() => Math.max(0, this.videoDuration() - this.trimmedDuration()));

  private draggingHandle: 'start' | 'end' | null = null;
  private readonly boundDragMove = this.onDragMove.bind(this);
  private readonly boundDragEnd  = this.onDragEnd.bind(this);

  // -- Trim (audio) ----------------------------------------------------------

  readonly audioTrimStart         = signal(0);
  readonly audioTrimEnd           = signal(0);
  readonly audioDuration          = signal(0);
  readonly audioTrimApplied       = signal(false);

  readonly audioTrimStartPercent      = computed(() => this.audioDuration() > 0 ? (this.audioTrimStart() / this.audioDuration()) * 100 : 0);
  readonly audioTrimEndPercent        = computed(() => this.audioDuration() > 0 ? (this.audioTrimEnd()   / this.audioDuration()) * 100 : 100);
  readonly audioTrimSelectionWidth    = computed(() => this.audioTrimEndPercent() - this.audioTrimStartPercent());
  readonly audioTrimEndExcludePercent = computed(() => 100 - this.audioTrimEndPercent());
  readonly audioTrimmedDuration       = computed(() => Math.max(0, this.audioTrimEnd()    - this.audioTrimStart()));
  readonly audioRemovedDuration       = computed(() => Math.max(0, this.audioDuration()   - this.audioTrimmedDuration()));

  private draggingAudioHandle: 'start' | 'end' | null = null;
  private readonly boundAudioDragMove = this.onAudioDragMove.bind(this);
  private readonly boundAudioDragEnd  = this.onAudioDragEnd.bind(this);

  // -- Thumbnail -------------------------------------------------------------

  readonly thumbnailPreviewUrl = signal<string | null>(null);
  readonly thumbnailFileName   = signal<string | null>(null);
  readonly thumbnailFileSize   = signal<string>('');
  readonly thumbnailError          = signal(false);
  readonly thumbnailTypeError      = signal(false);
  readonly thumbnailEmptyFileError = signal(false);
  readonly thumbnailDragOver       = signal(false);
  private thumbnailFile: File | null = null;

  // -- Audio playback --------------------------------------------------------

  readonly isAudioPlaying = signal(false);

  // -- Lifecycle -------------------------------------------------------------

  private readonly onBeforeUnload = (e: BeforeUnloadEvent): void => { e.preventDefault(); };
  private readonly onKeyDown = (e: KeyboardEvent): void => {
    const isRefresh = e.key === 'F5' || ((e.ctrlKey || e.metaKey) && e.key === 'r');
    if (isRefresh) { e.preventDefault(); this.showReloadWarning.set(true); }
  };

  async ngOnInit(): Promise<void> {
    window.addEventListener('beforeunload', this.onBeforeUnload);
    window.addEventListener('keydown', this.onKeyDown);
    this.layoutService.setHideHeader(true);

    // Detect page refresh: check for our navigation flag set by story-record.
    // On a real refresh, Angular doesn't re-run navigateToReview(), so the flag is absent.
    if (isPlatformBrowser(this.platformId)) {
      const wasNavigated = sessionStorage.getItem('fm_story_review_navigated');
      sessionStorage.removeItem('fm_story_review_navigated'); // consume immediately
      if (!wasNavigated) {
        sessionStorage.removeItem('fm_story_review_video_url');
        sessionStorage.removeItem('fm_story_record_questions');
        this.router.navigate(['/app/stories/record'], { replaceUrl: true });
        return;
      }
    }

    const navState       = (this.router.getCurrentNavigation()?.extras.state ?? history.state ?? {}) as StoryReviewNavState;
    const urlFromState   = navState.videoUrl ?? null;
    const titleFromState = navState.storyTitle ?? '';

    // title starts empty — required field
    if (navState.mediaType)           this.mediaType.set(navState.mediaType);
    if (navState.imageUrl)            this.imageUrl.set(navState.imageUrl);
    if (navState.categoryId)          this.categoryId.set(navState.categoryId);
    if (navState.questions?.length)   this.reviewQuestions.set(navState.questions);
    if (navState.questionTimeframes)  this.reviewTimeframes.set(navState.questionTimeframes);
    if (navState.videoDuration)       this.reviewVideoDuration.set(navState.videoDuration);

    // -- Transcripts: navState → sessionStorage → default placeholder -------
    let verbatimText  = navState.transcriptVerbatim  || '';
    let narrativeText = navState.transcriptNarrative || '';
    if (isPlatformBrowser(this.platformId)) {
      if (!verbatimText)  verbatimText  = sessionStorage.getItem('fm_story_review_transcript_verbatim')  ?? '';
      if (!narrativeText) narrativeText = sessionStorage.getItem('fm_story_review_transcript_narrative') ?? '';
    }
    if (!verbatimText)  verbatimText  = 'I remember this moment vividly. This auto-generated transcript can be edited before publishing.';
    if (!narrativeText) narrativeText = verbatimText;
    this.transcriptVerbatim.set(verbatimText);
    this.transcriptNarrative.set(narrativeText);

    if (urlFromState) {
      // Use URL from navigation state (blob or real URL — both valid during normal nav)
      this.videoUrl.set(urlFromState);
      if (isPlatformBrowser(this.platformId)) {
        sessionStorage.setItem('fm_story_review_video_url', urlFromState);
      }
    } else if (isPlatformBrowser(this.platformId)) {
      const cachedUrl = sessionStorage.getItem('fm_story_review_video_url');
      if (!cachedUrl) {
        // No URL available at all — redirect to step 1
        sessionStorage.removeItem('fm_story_review_video_url');
        sessionStorage.removeItem('fm_story_record_questions');
        this.router.navigate(['/app/stories/record'], { replaceUrl: true });
        return;
      }
      this.videoUrl.set(cachedUrl);
    }

    // Restore image URL for image-audio type
    if (!this.imageUrl() && isPlatformBrowser(this.platformId)) {
      const cachedImage = sessionStorage.getItem('fm_story_review_image_url');
      if (cachedImage) this.imageUrl.set(cachedImage);
    }

    await this.loadTaggingData();
  }

  ngAfterViewInit(): void {
    if (isPlatformBrowser(this.platformId)) {
      // Fallback: video may have loaded before event listeners were attached
      setTimeout(() => {
        const video = this.videoRef()?.nativeElement;
        if (video && video.readyState >= 1) this.resolveDuration(video);
      }, 200);
    }
  }

  dismissReloadWarning(): void { this.showReloadWarning.set(false); }

  confirmReload(): void {
    window.removeEventListener('beforeunload', this.onBeforeUnload);
    window.removeEventListener('keydown', this.onKeyDown);
    location.reload();
  }

  ngOnDestroy(): void {
    window.removeEventListener('beforeunload', this.onBeforeUnload);
    window.removeEventListener('keydown', this.onKeyDown);
    this.layoutService.setHideHeader(false);
    this.onDragEnd();
    this.onAudioDragEnd();
  }

  // -- Video metadata & trim init --------------------------------------------

  onVideoMetadataLoaded(): void {
    const video = this.videoRef()?.nativeElement;
    if (video) this.resolveDuration(video);
  }

  /**
   * MediaRecorder WebM blobs have duration = Infinity (no duration header).
   * Workaround: seek to a huge time; the browser clamps to the real end,
   * then we read currentTime from the seeked event.
   */
  private resolveDuration(video: HTMLVideoElement): void {
    if (isFinite(video.duration) && video.duration > 0) {
      this.initTrimFromDuration(video.duration);
      return;
    }
    const onSeeked = () => {
      video.removeEventListener('seeked', onSeeked);
      const realDuration = video.currentTime;
      video.currentTime  = 0;
      if (realDuration > 0) this.initTrimFromDuration(realDuration);
    };
    video.addEventListener('seeked', onSeeked);
    video.currentTime = 1e101; // browser clamps to actual end
  }

  private initTrimFromDuration(rawDuration: number): void {
    if (!isFinite(rawDuration) || rawDuration <= 0) return;
    const duration = parseFloat(rawDuration.toFixed(3));
    if (this.videoDuration() === duration) return;
    this.videoDuration.set(duration);
    this.trimStart.set(0);
    this.trimEnd.set(duration);
    this.cdr.markForCheck();
    setTimeout(() => this.generateTrimThumbnails(), 50);
  }

  // -- Trim controls ---------------------------------------------------------

  applyTrim(): void {
    const video = this.videoRef()?.nativeElement;
    if (!video || this.trimEnd() <= this.trimStart()) return;
    this.trimApplied.set(true);
    video.currentTime = this.trimStart();
    video.play().catch(() => {});
  }

  resetTrim(): void {
    this.trimApplied.set(false);
    this.trimStart.set(0);
    this.trimEnd.set(this.videoDuration());
    const video = this.videoRef()?.nativeElement;
    if (video) {
      video.pause();
      video.currentTime = 0;
    }
  }

  onVideoTimeUpdate(): void {
    const video = this.videoRef()?.nativeElement;
    if (!video) return;
    if (this.trimApplied()) {
      if (video.currentTime < this.trimStart()) {
        video.currentTime = this.trimStart();
      } else if (video.currentTime >= this.trimEnd()) {
        video.pause();
        video.currentTime = this.trimStart(); // loop back to trim start
      }
    }
  }

    onVideoSeeked(): void {
    const video = this.videoRef()?.nativeElement;
    if (!video || !this.trimApplied()) return;
    if (video.currentTime < this.trimStart()) {
      video.currentTime = this.trimStart();
    } else if (video.currentTime > this.trimEnd()) {
      video.currentTime = this.trimEnd();
    }
  }

  // -- Trim thumbnail strip --------------------------------------------------

  generateTrimThumbnails(): void {
    if (!isPlatformBrowser(this.platformId)) return;
    const url      = this.videoUrl();
    const duration = this.videoDuration();
    const canvas   = this.trimCanvas()?.nativeElement;
    if (!url || !duration || !canvas) return;

    const THUMB_COUNT = 12;
    const THUMB_H     = 56;
    const THUMB_W     = Math.round(THUMB_H * (16 / 9));
    canvas.width  = THUMB_COUNT * THUMB_W;
    canvas.height = THUMB_H;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const vid = document.createElement('video');
    vid.src     = url;
    vid.muted   = true;
    vid.preload = 'auto';
    (vid as HTMLVideoElement & { playsInline: boolean }).playsInline = true;
    // Do NOT set crossOrigin for blob URLs — it taints the canvas.

    let index = 0;
    const captureNext = () => {
      if (index >= THUMB_COUNT) { this.cdr.markForCheck(); return; }
      vid.currentTime = index === 0 ? 0.01 : (index / (THUMB_COUNT - 1)) * duration;
    };

    vid.addEventListener('seeked',         () => { try { ctx.drawImage(vid, index * THUMB_W, 0, THUMB_W, THUMB_H); } catch (_) {} index++; captureNext(); });
    vid.addEventListener('loadedmetadata', captureNext);
    vid.addEventListener('error',          () => this.cdr.markForCheck());
    vid.load();
  }

  // -- Drag handles ----------------------------------------------------------

  startDragging(handle: 'start' | 'end', event: MouseEvent | TouchEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.draggingHandle = handle;
    this.zone.runOutsideAngular(() => {
      document.addEventListener('mousemove', this.boundDragMove);
      document.addEventListener('mouseup',   this.boundDragEnd);
      document.addEventListener('touchmove', this.boundDragMove, { passive: false });
      document.addEventListener('touchend',  this.boundDragEnd);
    });
  }

  onDragMove(event: MouseEvent | TouchEvent): void {
    if (!this.draggingHandle) return;
    const container = this.trimContainer()?.nativeElement;
    if (!container) return;
    const rect    = container.getBoundingClientRect();
    const clientX = event instanceof MouseEvent ? event.clientX : event.touches[0].clientX;
    const ratio   = Math.max(0, Math.min(1, (clientX - rect.left) / rect.width));
    const dur     = this.videoDuration();
    if (!dur) return;
    const time = parseFloat((ratio * dur).toFixed(3));
    this.zone.run(() => {
      if (this.draggingHandle === 'start') {
        this.trimStart.set(Math.min(time, Math.max(0, this.trimEnd() - 0.5)));
      } else {
        this.trimEnd.set(Math.max(this.trimStart() + 0.5, Math.min(time, dur)));
      }
      this.trimApplied.set(false);
      this.cdr.markForCheck();
    });
  }

  onDragEnd(): void {
    this.draggingHandle = null;
    document.removeEventListener('mousemove', this.boundDragMove);
    document.removeEventListener('mouseup',   this.boundDragEnd);
    document.removeEventListener('touchmove', this.boundDragMove);
    document.removeEventListener('touchend',  this.boundDragEnd);
  }

  // -- Audio metadata & trim -------------------------------------------------

  onAudioMetadataLoaded(): void {
    const audio = this.audioRef()?.nativeElement;
    if (audio) this.resolveAudioDuration(audio);
  }

  private resolveAudioDuration(audio: HTMLAudioElement): void {
    if (isFinite(audio.duration) && audio.duration > 0) {
      this.initAudioTrimFromDuration(audio.duration);
      return;
    }
    const onSeeked = () => {
      audio.removeEventListener('seeked', onSeeked);
      const realDuration = audio.currentTime;
      audio.currentTime  = 0;
      if (realDuration > 0) this.initAudioTrimFromDuration(realDuration);
    };
    audio.addEventListener('seeked', onSeeked);
    audio.currentTime = 1e101;
  }

  private initAudioTrimFromDuration(rawDuration: number): void {
    if (!isFinite(rawDuration) || rawDuration <= 0) return;
    const duration = parseFloat(rawDuration.toFixed(3));
    if (this.audioDuration() === duration) return;
    this.audioDuration.set(duration);
    this.audioTrimStart.set(0);
    this.audioTrimEnd.set(duration);
    this.cdr.markForCheck();
  }

  applyAudioTrim(): void {
    const audio = this.audioRef()?.nativeElement;
    if (!audio || this.audioTrimEnd() <= this.audioTrimStart()) return;
    this.audioTrimApplied.set(true);
    audio.currentTime = this.audioTrimStart();
    audio.play().catch(() => {});
  }

  resetAudioTrim(): void {
    this.audioTrimApplied.set(false);
    this.audioTrimStart.set(0);
    this.audioTrimEnd.set(this.audioDuration());
    const audio = this.audioRef()?.nativeElement;
    if (audio) { audio.pause(); audio.currentTime = 0; }
  }

  onAudioTimeUpdate(): void {
    const audio = this.audioRef()?.nativeElement;
    if (!audio || !this.audioTrimApplied()) return;
    if (audio.currentTime < this.audioTrimStart()) {
      audio.currentTime = this.audioTrimStart();
    } else if (audio.currentTime >= this.audioTrimEnd()) {
      audio.pause();
      audio.currentTime = this.audioTrimStart();
    }
  }

  onAudioPlay():  void { this.isAudioPlaying.set(true);  this.cdr.markForCheck(); }
  onAudioPause(): void { this.isAudioPlaying.set(false); this.cdr.markForCheck(); }

  startAudioDragging(handle: 'start' | 'end', event: MouseEvent | TouchEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.draggingAudioHandle = handle;
    this.zone.runOutsideAngular(() => {
      document.addEventListener('mousemove', this.boundAudioDragMove);
      document.addEventListener('mouseup',   this.boundAudioDragEnd);
      document.addEventListener('touchmove', this.boundAudioDragMove, { passive: false });
      document.addEventListener('touchend',  this.boundAudioDragEnd);
    });
  }

  onAudioDragMove(event: MouseEvent | TouchEvent): void {
    if (!this.draggingAudioHandle) return;
    const container = this.audioTrimContainer()?.nativeElement;
    if (!container) return;
    const rect    = container.getBoundingClientRect();
    const clientX = event instanceof MouseEvent ? event.clientX : event.touches[0].clientX;
    const ratio   = Math.max(0, Math.min(1, (clientX - rect.left) / rect.width));
    const dur     = this.audioDuration();
    if (!dur) return;
    const time = parseFloat((ratio * dur).toFixed(3));
    this.zone.run(() => {
      if (this.draggingAudioHandle === 'start') {
        this.audioTrimStart.set(Math.min(time, Math.max(0, this.audioTrimEnd() - 0.5)));
      } else {
        this.audioTrimEnd.set(Math.max(this.audioTrimStart() + 0.5, Math.min(time, dur)));
      }
      this.audioTrimApplied.set(false);
      this.cdr.markForCheck();
    });
  }

  onAudioDragEnd(): void {
    this.draggingAudioHandle = null;
    document.removeEventListener('mousemove', this.boundAudioDragMove);
    document.removeEventListener('mouseup',   this.boundAudioDragEnd);
    document.removeEventListener('touchmove', this.boundAudioDragMove);
    document.removeEventListener('touchend',  this.boundAudioDragEnd);
  }

  // -- Thumbnail -------------------------------------------------------------

  onThumbnailUpload(event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    if (!file) return;
    input.value = '';
    this.processThumbnailFile(file);
  }

  removeThumbnail(): void {
    this.thumbnailFile        = null;
    this.thumbnailPreviewUrl.set(null);
    this.thumbnailFileName.set(null);
    this.thumbnailFileSize.set('');
  }

  onThumbnailDragOver(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.thumbnailDragOver.set(true);
  }

  onThumbnailDragLeave(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.thumbnailDragOver.set(false);
  }

  onThumbnailDrop(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.thumbnailDragOver.set(false);
    const file = event.dataTransfer?.files?.[0];
    if (!file) return;
    this.processThumbnailFile(file);
  }

  private processThumbnailFile(file: File): void {
    if (file.size === 0) {
      this.thumbnailEmptyFileError.set(true);
      this.thumbnailTypeError.set(false);
      return;
    }
    this.thumbnailEmptyFileError.set(false);
    const allowed = ['image/jpeg', 'image/png', 'image/webp', 'image/gif'];
    if (!allowed.includes(file.type)) {
      this.thumbnailTypeError.set(true);
      return;
    }
    this.thumbnailTypeError.set(false);
    this.thumbnailFile = file;
    this.thumbnailFileName.set(file.name);
    this.thumbnailFileSize.set(`${(file.size / 1024).toFixed(0)} KB`);
    this.thumbnailError.set(false);
    const reader = new FileReader();
    reader.onload = (e) => {
      this.thumbnailPreviewUrl.set(e.target?.result as string);
      this.cdr.markForCheck();
    };
    reader.readAsDataURL(file);
  }

  // -- Tagging ---------------------------------------------------------------

  readonly collapsedTagFamilyIds = signal<string[]>([]);

  toggleTagFamily(familySpaceId: string): void {
    this.collapsedTagFamilyIds.update(ids =>
      ids.includes(familySpaceId) ? ids.filter(id => id !== familySpaceId) : [...ids, familySpaceId],
    );
  }

  isTagFamilyExpanded(familySpaceId: string): boolean {
    return !this.collapsedTagFamilyIds().includes(familySpaceId);
  }

  selectFamily(familySpaceId: string): void {
    // Only changes the view filter — individual checkbox states are never touched.
    this.selectedFamilySpaceId.set(familySpaceId);
  }

  selectSharingFamily(familySpaceId: string): void {
    // Only changes the view filter — individual checkbox states are never touched.
    this.selectedSharingFamilyId.set(familySpaceId);
    this.sharingMemberError.set(null);
  }

  toggleTaggedMember(memberId: string): void {
    this.selectedTaggedMemberIds.update(ids =>
      ids.includes(memberId) ? ids.filter(id => id !== memberId) : [...ids, memberId],
    );
  }

  isTagged(memberId: string): boolean { return this.selectedTaggedMemberIds().includes(memberId); }

  // -- Sharing ---------------------------------------------------------------

  setSharingOption(option: SharingOption): void {
    this.sharingOption.set(option);
    this.sharingMemberError.set(null);
  }

  toggleSharedMember(memberId: string): void {
    this.selectedSharedMemberIds.update(ids =>
      ids.includes(memberId) ? ids.filter(id => id !== memberId) : [...ids, memberId],
    );
    this.sharingMemberError.set(null);
  }

  isShared(memberId: string): boolean { return this.selectedSharedMemberIds().includes(memberId); }

  // -- Story details ---------------------------------------------------------

  onToneChange(event: Event):    void { this.emotionalToneId.set(Number((event.target as HTMLSelectElement).value)); }

  // -- Transcript ------------------------------------------------------------

  setTranscriptTab(tab: TranscriptTab): void { this.transcriptTab.set(tab); }

  toggleTranscriptEdit(): void { this.transcriptEditMode.update(v => !v); }

  onTranscriptInput(event: Event): void {
    const value = (event.target as HTMLTextAreaElement).value;
    if (this.transcriptTab() === 'verbatim') this.transcriptVerbatim.set(value);
    else                                      this.transcriptNarrative.set(value);
  }

  // -- Member helpers --------------------------------------------------------

  getMemberPhotoUrl(member: FamilyMember): string { return member.image?.trim() || ''; }

  hasMemberImage(member: FamilyMember): boolean {
    return !!this.getMemberPhotoUrl(member) && !this.memberImageErrors()[member.familyMemberId];
  }

  onMemberImageError(memberId: string): void {
    this.memberImageErrors.update(map => ({ ...map, [memberId]: true }));
  }

  getMemberInitials(member: FamilyMember): string {
    const parts = (member.fullName || '').trim().split(/\s+/).filter(Boolean);
    const first = parts[0]?.charAt(0) ?? '';
    const last  = parts.length > 1 ? parts[parts.length - 1]?.charAt(0) ?? '' : '';
    return (first + last).toUpperCase() || 'NA';
  }

  /** Returns the family name, looking it up from familySpaces if the member field is blank. */
  getFamilyName(member: FamilyMember): string {
    if (member.familyName?.trim()) return member.familyName;
    return this.familySpaces().find(f => f.familySpaceId === member.familySpaceId)?.familyName ?? '';
  }

  // -- Formatting ------------------------------------------------------------

  formatRecordingTime(seconds: number): string {
    if (!isFinite(seconds) || isNaN(seconds) || seconds < 0) return '00:00';
    const m = Math.floor(seconds / 60).toString().padStart(2, '0');
    const s = Math.floor(seconds % 60).toString().padStart(2, '0');
    return `${m}:${s}`;
  }

  todayIso(): string {
    return new Date().toISOString().split('T')[0];
  }

  // -- Navigation ------------------------------------------------------------

  backToRecording(): void {
    this.router.navigate(['/app/stories/record'], { state: { returnFromReview: true } });
  }

  openPublishConfirm(): void {
    if (!this.storyTitle().trim()) {
      this.titleError.set(true);
      const el = document.getElementById('storyTitleInput');
      el?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      el?.focus();
      return;
    }
    this.titleError.set(false);

    if (!this.storyDate() || !!this.storyDateError()) {
      if (!this.storyDate()) this.storyDateError.set('Please enter a valid date.');
      const el = document.querySelector<HTMLElement>('input[type="date"]');
      el?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      el?.focus();
      return;
    }

    const isAudioType = this.mediaType() === 'audio' || this.mediaType() === 'image-audio';
    if (isAudioType && !this.thumbnailPreviewUrl()) {
      this.thumbnailError.set(true);
      const el = document.getElementById('fmReviewThumbnail');
      el?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      return;
    }
    this.thumbnailError.set(false);

    if (this.sharingOption() === 'family' && this.activeSharedIds().length === 0) {
      this.sharingMemberError.set('Please select at least one family member to share this story with.');
      document.getElementById('fmReviewSharing')?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      return;
    }
    this.sharingMemberError.set(null);

    this.showPublishConfirm.set(true);
  }

  cancelPublish(): void { this.showPublishConfirm.set(false); }

  async saveAndPublish(): Promise<void> {
    this.showPublishConfirm.set(false);
    this.isPublishing.set(true);
    this.publishError.set(null);

    try {
      const isAudioType = this.mediaType() === 'audio' || this.mediaType() === 'image-audio';

      // -- Client-side trim: re-encode just the [start, end] segment --------
      // This produces a new blob that only contains the kept portion so the
      // S3-stored file is already trimmed. Falls back gracefully on Safari
      // (captureStream not supported) — in that case the full blob is uploaded
      // and trimStart/trimEnd metadata tells the backend where to cut.
      let blobUrl = this.videoUrl() ?? '';
      let trimmedBlobUrl: string | null = null;
      let clientTrimSucceeded = false;

      const hasTrim = isAudioType
        ? (this.audioTrimApplied() && (this.audioTrimStart() > 0.05 || this.audioTrimEnd() < this.audioDuration() - 0.05))
        : (this.trimApplied()      && (this.trimStart()      > 0.05 || this.trimEnd()      < this.videoDuration()  - 0.05));

      if (hasTrim && blobUrl.startsWith('blob:')) {
        const start  = isAudioType ? this.audioTrimStart() : this.trimStart();
        const end    = isAudioType ? this.audioTrimEnd()   : this.trimEnd();
        const elType = isAudioType ? 'audio' : 'video';
        this.isTrimmingMedia.set(true);
        this.trimmingProgress.set(0);
        this.cdr.markForCheck();
        try {
          trimmedBlobUrl    = await this.createTrimmedBlob(blobUrl, start, end, elType as 'video' | 'audio');
          blobUrl           = trimmedBlobUrl;
          clientTrimSucceeded = true;
        } catch {
          // captureStream not available or failed — fall back to full blob + metadata trim
          trimmedBlobUrl = null;
        } finally {
          this.isTrimmingMedia.set(false);
          this.cdr.markForCheck();
        }
      }

      // -- Upload video/audio to S3 before creating the story ---------------
      let mediaFileName = '';
      if (blobUrl.startsWith('blob:')) {
        mediaFileName = await this.s3Upload.uploadStoryMedia(blobUrl, this.mediaType());
      } else {
        // Already a real URL or filename — extract just the filename
        mediaFileName = blobUrl.split('/').pop() ?? blobUrl;
      }

      // Free the trimmed blob URL now that it has been uploaded
      if (trimmedBlobUrl) {
        URL.revokeObjectURL(trimmedBlobUrl);
        trimmedBlobUrl = null;
      }

      // -- Upload thumbnail (audio / image-audio types) if provided ---------
      let thumbnailFileName: string | undefined;
      if (this.thumbnailFile) {
        const thumbBlobUrl = URL.createObjectURL(this.thumbnailFile);
        try {
          thumbnailFileName = await this.s3Upload.uploadStoryMedia(thumbBlobUrl, this.mediaType());
        } finally {
          URL.revokeObjectURL(thumbBlobUrl);
        }
      }

      const mediaTypeMap: Record<string, number> = { video: 1, audio: 2, 'image-audio': 3 };
      const mediaTypeNum = mediaTypeMap[this.mediaType().toLowerCase()] ?? 1;

      const sharingMap: Record<string, number> = { private: 1, family: 2, public: 3 };
      const visibilityTypeId = sharingMap[this.sharingOption()] ?? 3;

      const questions = this.reviewQuestions().map((q, idx) => {
        const tf = this.reviewTimeframes()[q.id];
        return {
          questionId:     q.id,
          questionSource: q.sourceType ?? 1,
          questionOrder:  idx + 1,
          startTime:      tf?.startSecond ?? 0,
          endTime:        tf?.endSecond   ?? 0,
        };
      });

      // isAudioType is declared at the top of the try block

      // When client-side trim succeeded the uploaded blob is already cut, so
      // trimStart = 0 and trimEnd = trimmed duration. Otherwise fall back to
      // sending the original trim handles as metadata for the backend.
      const trimmedDur = isAudioType ? this.audioTrimmedDuration() : this.trimmedDuration();
      const effectiveTrimStart = clientTrimSucceeded ? 0
        : isAudioType
          ? (this.audioTrimApplied() ? this.audioTrimStart() : 0)
          : (this.trimApplied()      ? this.trimStart()      : 0);
      const effectiveTrimEnd = clientTrimSucceeded ? trimmedDur
        : isAudioType
          ? (this.audioTrimApplied() ? this.audioTrimEnd()   : (this.audioDuration() || this.reviewVideoDuration()))
          : (this.trimApplied()      ? this.trimEnd()        : (this.reviewVideoDuration() || this.videoDuration()));

      // activeSharedIds returns the full selection set regardless of which filter tab is open.
      const activeSharedIds = this.activeSharedIds();

      const payload: CreateAndPublishStoryRequest = {
        categoryId:             this.categoryId(),
        mediaType:              mediaTypeNum,
        questions,
        title:                  this.storyTitle().trim(),
        mediaFileName,
        thumbnailFileName,
        duration:               clientTrimSucceeded ? trimmedDur
                                  : (this.reviewVideoDuration() || (isAudioType ? this.audioDuration() : this.videoDuration())),
        storyDate:              this.storyDate() || new Date().toISOString().split('T')[0],
        location:               this.storyLocation(),
        emotionalToneId:        this.emotionalToneId() || null,
        visibilityTypeId,
        trimStart:              effectiveTrimStart,
        trimEnd:                effectiveTrimEnd,
        taggedFamilyMemberIds:  this.selectedTaggedMemberIds().length > 0 ? this.selectedTaggedMemberIds() : null,
        sharedWithUserIds:      visibilityTypeId === 2 && activeSharedIds.length > 0
                                  ? activeSharedIds
                                  : null,
      };

      const res = await this.api.request<{ storyId: string }>(
        'POST', API_ROUTES.CREATE_AND_PUBLISH_STORY, payload,
        { showLoader: true, showToaster: true, useToken: true },
      );

      if (res.status) {
        // Clean up session storage
        sessionStorage.removeItem('fm_story_review_video_url');
        sessionStorage.removeItem('fm_story_record_questions');
        sessionStorage.removeItem('fm_story_review_transcript_verbatim');
        sessionStorage.removeItem('fm_story_review_transcript_narrative');
        window.removeEventListener('beforeunload', this.onBeforeUnload);
        window.removeEventListener('keydown', this.onKeyDown);
        this.router.navigate(['/app/stories']);
      } else {
        this.publishError.set(res.message ?? 'Failed to publish story. Please try again.');
        this.isPublishing.set(false);
      }
    } catch (err) {
      this.publishError.set('An unexpected error occurred. Please try again.');
      this.isPublishing.set(false);
    }
  }

  // -- Private: client-side media trim -------------------------------------

  /**
   * Re-encodes just the [start, end] portion of a blob URL using
   * HTMLMediaElement.captureStream() + MediaRecorder.
   *
   * - Works in Chrome, Firefox, Edge (all support captureStream).
   * - Falls back to the original blobUrl on Safari (no captureStream),
   *   so the backend will use the trimStart/trimEnd metadata instead.
   * - The element is created off-screen, volume = 0, so no audio leaks.
   *
   * @returns a new blob: URL for the trimmed segment (caller must revoke it).
   */
  private async createTrimmedBlob(
    blobUrl: string,
    start: number,
    end: number,
    elementType: 'video' | 'audio',
  ): Promise<string> {
    if (!isPlatformBrowser(this.platformId)) return blobUrl;

    // Feature-detect captureStream before creating the real element
    const test = document.createElement(elementType);
    if (typeof (test as any).captureStream !== 'function') {
      return blobUrl; // Safari — skip client-side trim
    }

    const el = document.createElement(elementType) as HTMLVideoElement | HTMLAudioElement;
    el.src = blobUrl;
    el.volume = 0;             // silent background re-encode
    (el as any).playsInline = true;

    await new Promise<void>((resolve, reject) => {
      el.onloadedmetadata = () => resolve();
      el.onerror = () => reject(new Error('Failed to load media for trimming'));
      el.load();
    });

    const stream: MediaStream = (el as any).captureStream();
    const chunks: Blob[] = [];

    // Pick the best supported MIME type
    const mimes = elementType === 'video'
      ? ['video/webm;codecs=vp9,opus', 'video/webm;codecs=vp8,opus', 'video/webm']
      : ['audio/webm;codecs=opus', 'audio/webm'];
    const mimeType = mimes.find(m => MediaRecorder.isTypeSupported(m)) ?? '';

    const recorder = new MediaRecorder(stream, mimeType ? { mimeType } : undefined);
    recorder.ondataavailable = (e: BlobEvent) => { if (e.data.size > 0) chunks.push(e.data); };

    const duration = Math.max(0.1, end - start);

    return new Promise<string>((resolve, reject) => {
      recorder.onstop = () => {
        el.src = '';
        stream.getTracks().forEach(t => t.stop());
        const trimmedBlob = new Blob(chunks, {
          type: recorder.mimeType || (elementType === 'video' ? 'video/webm' : 'audio/webm'),
        });
        resolve(URL.createObjectURL(trimmedBlob));
      };
      recorder.onerror = () => reject(new Error('MediaRecorder error during trim'));

      // Seek to the trim start, then start recording once seeked
      el.currentTime = start;
      const onSeeked = () => {
        el.removeEventListener('seeked', onSeeked);
        recorder.start(100);
        el.play().catch(reject);

        // Update progress
        const progressInterval = setInterval(() => {
          const elapsed = Math.max(0, el.currentTime - start);
          this.zone.run(() => {
            this.trimmingProgress.set(Math.min(99, Math.round((elapsed / duration) * 100)));
            this.cdr.markForCheck();
          });
        }, 200);

        // Stop recording when we reach the trim end
        const stopInterval = setInterval(() => {
          if (el.currentTime >= end - 0.05) {
            clearInterval(stopInterval);
            clearInterval(progressInterval);
            el.pause();
            recorder.stop();
            this.zone.run(() => { this.trimmingProgress.set(100); this.cdr.markForCheck(); });
          }
        }, 50);
      };
      el.addEventListener('seeked', onSeeked);
    });
  }

  // -- Private: data loading -------------------------------------------------

  private async loadTaggingData(): Promise<void> {
    const [familyRes, memberRes, toneRes] = await Promise.all([
      this.api.request<FamilySpace[]>   ('GET', API_ROUTES.GET_ACTIVE_FAMILY_LIST, undefined, { showLoader: false, showToaster: false }),
      this.api.request<FamilyMember[]>  ('GET', API_ROUTES.GET_FAMILY_MEMBER_LIST, undefined, { showLoader: false, showToaster: false }),
      this.api.request<EmotionalTone[]> ('GET', API_ROUTES.GET_EMOTIONAL_TONES,    undefined, { showLoader: false, showToaster: false }),
    ]);
    if (familyRes.status && familyRes.data) this.familySpaces.set(familyRes.data);
    if (memberRes.status && memberRes.data) {
      this.familyMembers.set(memberRes.data);
      // Default: "All Families" selected → pre-select every member in both sections.
      const allIds = memberRes.data.map(m => m.familyMemberId);
      this.selectedTaggedMemberIds.set(allIds);
      this.selectedSharedMemberIds.set(allIds);
    }
    if (toneRes.status && toneRes.data) this.emotionalTones.set(toneRes.data);
  }
}